import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh, Storefront } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { MlOrder } from '../../types';

const PAGE_SIZE = 50;
const OAUTH_STATE_KEY = 'ml_oauth_state';

const STATUS: Record<string, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  received: { label: 'Recibida', color: 'default' },
  processing: { label: 'Procesando', color: 'info' },
  success: { label: 'Exitosa', color: 'success' },
  failed: { label: 'Fallida', color: 'error' },
  ignored: { label: 'Sin pagar', color: 'warning' },
};

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-AR') : '');
const fmtArs = (n: number | null) =>
  n === null ? '' : n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

export const MlOrderList: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<MlOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    // No setError(null) here: it would wipe an OAuth-return error set on the
    // same mount. The alert is dismissible.
    setLoading(true);
    try {
      const res = await apiService.ml.listOrders({
        status: status || undefined,
        q: q.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setOrders(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar las transacciones.');
    } finally {
      setLoading(false);
    }
  }, [status, q, dateFrom, dateTo, page]);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce the search box
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    apiService.ml.oauthStatus().then((s) => setConnected(s.connected)).catch(() => setConnected(null));
  }, []);

  // OAuth return: ML redirects back here with ?code=&state=.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (!code || !isAdmin) return;
    const expected = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    navigate('/ml', { replace: true });
    if (!expected || params.get('state') !== expected) {
      setError('La autorización de Mercado Libre no coincide con la solicitada; intentá de nuevo.');
      return;
    }
    apiService.ml
      .oauthExchange(code)
      .then(() => setConnected(true))
      .catch((e: any) => setError(e?.response?.data?.detail || 'No se pudo conectar Mercado Libre.'));
  }, [location.search, isAdmin, navigate]);

  const connect = async () => {
    try {
      const { url, state } = await apiService.ml.oauthStart();
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
      window.location.href = url;
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo iniciar la conexión con Mercado Libre.');
    }
  };

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Storefront />
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Facturación Mercado Libre
        </Typography>
        {isAdmin && connected === false && (
          <Button variant="contained" onClick={connect}>
            Conectar Mercado Libre
          </Button>
        )}
        {connected && <Chip size="small" color="success" label="Mercado Libre conectado" />}
        <Button startIcon={<Refresh />} onClick={load} disabled={loading}>
          Actualizar
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Buscar (orden, documento, razón social)"
          value={q}
          onChange={(e) => resetPage(setQ)(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={status} onChange={(e) => resetPage(setStatus)(e.target.value as string)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Desde"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => resetPage(setDateFrom)(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => resetPage(setDateTo)(e.target.value)}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Orden ML</TableCell>
              <TableCell>Comprador</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Factura</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>NP</TableCell>
              <TableCell>NPH</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Sin transacciones
                </TableCell>
              </TableRow>
            ) : (
              orders.map((o) => {
                const st = STATUS[o.status] || { label: o.status, color: 'default' as const };
                const chip = <Chip size="small" label={st.label} color={st.color} />;
                return (
                  <TableRow key={o.id} hover>
                    <TableCell>{fmtDate(o.created_at)}</TableCell>
                    <TableCell>{o.ml_order_id}</TableCell>
                    <TableCell>{o.razon_social}</TableCell>
                    <TableCell>{o.buyer_doc_number ? `${o.buyer_doc_type ?? ''} ${o.buyer_doc_number}` : ''}</TableCell>
                    <TableCell>{o.invoice_type}</TableCell>
                    <TableCell align="right">{fmtArs(o.total_ars)}</TableCell>
                    <TableCell>{o.erp_np_document_id}</TableCell>
                    <TableCell>{o.erp_nph_document_id}</TableCell>
                    <TableCell>{o.error ? <Tooltip title={o.error}>{chip}</Tooltip> : chip}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};
